import { balanceMessage as sharedBalanceMessage } from "@rutamagica/contracts";
import { ExternalLink, MessageCircle, Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useDevelopmentFixtures, whatsappApi } from "../../api/client";
import { Button, Dialog } from "../../components/ui";
import { mexicoDateKey } from "../../data/format";
import { money } from "./shared";

/** The reminder that goes with the balance link: the visit day, what is left to pay and the link. */
export function balanceMessage(input: Parameters<typeof sharedBalanceMessage>[0], today = mexicoDateKey()) {
  return sharedBalanceMessage(input, today);
}

/**
 * The WooCommerce order for the rest (created when the advance was paid online): open it, or send
 * its link to the customer by WhatsApp with a short reminder of the visit.
 */
export function BalanceLink({ summary, service, date, time, customerName, phone, conversationId, canSend, currency }: {
  summary: Record<string, unknown> | null | undefined;
  service: string;
  date: string;
  time: string;
  customerName: string;
  phone: string | null | undefined;
  conversationId: string | null;
  canSend: boolean;
  currency: string;
}) {
  const balance = Number(summary?.balance_due ?? 0);
  const url = typeof summary?.balance_payment_url === "string" && /^https?:\/\//.test(summary.balance_payment_url) ? summary.balance_payment_url : "";
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  if (!(balance > 0) || !url) return null;
  const reachable = canSend && Boolean(conversationId || phone);

  const start = () => {
    setFeedback("");
    setBody(balanceMessage({ name: customerName, service, date, time, balance, total: Number(summary?.contract_total ?? 0), currency, url }));
    setOpen(true);
  };
  const send = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setFeedback("");
    try {
      if (!useDevelopmentFixtures) {
        if (conversationId) await whatsappApi.reply(conversationId, body.trim());
        else await whatsappApi.start(phone!, body.trim(), customerName);
      }
      setOpen(false);
      setFeedback("Enlace del saldo enviado por WhatsApp.");
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : "No se pudo enviar el mensaje.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="balance-link">
      <div className="case-actions">
        <a className="button button--secondary" href={url} target="_blank" rel="noreferrer">
          <ExternalLink size={16} aria-hidden="true" /> Abrir enlace para liquidar
        </a>
        {reachable && <Button icon={MessageCircle} onClick={start}>Enviar enlace por WhatsApp</Button>}
      </div>
      {feedback && <p role="status" className="report-explainer">{feedback}</p>}
      <Dialog open={open} size="compact" title="Enviar enlace del saldo" eyebrow={money(balance, currency)} onClose={() => setOpen(false)}>
        <form className="booking-action-form" onSubmit={(event) => void send(event)}>
          <label className="field">
            <span className="field__label">Mensaje</span>
            <span className="field__control">
              <textarea aria-label="Mensaje" rows={11} maxLength={4000} value={body} onChange={(event) => setBody(event.target.value)} />
            </span>
          </label>
          <p className="report-explainer">El enlace abre el pago del saldo en la página (Stripe). Al pagarlo, la reserva queda liquidada sola.</p>
          <Button type="submit" icon={Send} disabled={busy || !body.trim()}>{busy ? "Enviando…" : "Enviar por WhatsApp"}</Button>
        </form>
      </Dialog>
    </div>
  );
}
